import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

import BreadcrumbNavigation from '../../components/navigation/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import activityService from '../../services/activityService';
import ActivityFilters from './components/ActivityFilters';
import ActivityTimeline from './components/ActivityTimeline';
import VAHeaderNav from '../../components/navigation/VAHeaderNav';

const VAActivitiesPage = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activities, setActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [filters, setFilters] = useState({
    types: [],
    startDate: null,
    endDate: null,
    search: '',
  });

  // Activity types definition
  const activityTypesList = useMemo(() => [
    { value: 'application', label: 'Applications', icon: 'Send' },
    { value: 'match', label: 'Job Matches', icon: 'Heart' },
    { value: 'message', label: 'Messages', icon: 'MessageSquare' },
    { value: 'profile_view', label: 'Profile Views', icon: 'Eye' },
    { value: 'skill_assessment', label: 'Skill Assessments', icon: 'Award' },
    { value: 'interview', label: 'Interviews', icon: 'Video' },
    { value: 'job_save', label: 'Saved Jobs', icon: 'Bookmark' },
    { value: 'recommendation', label: 'Recommendations', icon: 'ThumbsUp' },
    { value: 'system_notification', label: 'System Notifications', icon: 'Bell' }
  ], []);

  const fetchAllActivities = useCallback(async () => {
    if (!userProfile?.id) return;
    
    try {
      setLoading(true);
      setError('');

      // Get all activities without limit
      const result = await activityService.getRecentActivities(userProfile.id, 100);
      if (result.success) {
        setActivities(result.data || []);
        setFilteredActivities(result.data || []);
      } else {
        setError(result.error || 'Failed to load activities');
      }
    } catch (err) {
      setError('Failed to load activities. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  }, [userProfile?.id]);

  useEffect(() => {
    if (user?.id && userProfile?.role === 'va') {
      fetchAllActivities();
    }
  }, [user?.id, userProfile?.role, fetchAllActivities]);

  // Apply filters to activities
  const applyFilters = useCallback(() => {
    if (!activities.length) {
      setFilteredActivities([]);
      return;
    }

    let result = [...activities];

    // Filter by activity type
    if (filters.types.length > 0) {
      result = result.filter(activity => filters.types.includes(activity.type));
    }

    // Filter by date range
    if (filters.startDate) {
      const startDate = new Date(filters.startDate);
      result = result.filter(activity => {
        const activityDate = new Date(activity.timestamp);
        return activityDate >= startDate;
      });
    }

    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999); // End of the day
      result = result.filter(activity => {
        const activityDate = new Date(activity.timestamp);
        return activityDate <= endDate;
      });
    }

    // Filter by search term
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      result = result.filter(activity => 
        activity.description?.toLowerCase().includes(searchTerm) ||
        activity.title?.toLowerCase().includes(searchTerm) ||
        (activity.metadata?.company?.toLowerCase().includes(searchTerm)) ||
        (activity.metadata?.job_title?.toLowerCase().includes(searchTerm))
      );
    }

    setFilteredActivities(result);
  }, [filters, activities]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      types: [],
      startDate: null,
      endDate: null,
      search: '',
    });
  }, []);

  const handleActivityClick = useCallback((activity) => {
    // Navigate based on activity type
    switch (activity.type) {
      case 'application':
        navigate('/applications');
        break;
      case 'match':
        navigate(`/job/${activity.metadata?.jobId || ''}`);
        break;
      case 'message':
        navigate('/messages');
        break;
      case 'profile_view':
        navigate('/va-profile');
        break;
      case 'interview':
        navigate('/interviews');
        break;
      case 'job_save':
        navigate('/saved-jobs');
        break;
      case 'skill_assessment':
        navigate('/skills-assessment');
        break;
      default:
        navigate('/va-dashboard');
        break;
    }
  }, [navigate]);

  // Custom breadcrumbs
  const customBreadcrumbs = useMemo(() => {
    return [
      {
        label: 'Dashboard',
        path: '/va-dashboard',
        icon: 'LayoutDashboard',
        isActive: false
      },
      {
        label: 'Activity History',
        path: '/activities',
        icon: 'Clock',
        isActive: true
      }
    ];
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <VAHeaderNav />
      <BreadcrumbNavigation customBreadcrumbs={customBreadcrumbs} />
      
      <main className="pt-8 pb-20 md:pb-8">
        <div className="content-container">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div>
                <div className="flex items-center space-x-2 mb-1">
                  <button
                    onClick={() => navigate('/va-dashboard')}
                    className="text-text-secondary hover:text-primary"
                  >
                    <Icon name="ArrowLeft" size={20} />
                  </button>
                  <h1 className="text-2xl lg:text-3xl font-bold text-text-primary">
                    Activity History
                  </h1>
                </div>
                <p className="text-text-secondary">
                  Track all your job search activities and interactions with employers
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button 
                  variant="outline" 
                  iconName="RefreshCw" 
                  onClick={fetchAllActivities}
                  isLoading={loading}
                >
                  Refresh
                </Button>
                <Button 
                  variant="primary" 
                  iconName="Filter" 
                  onClick={() => document.getElementById('filters-section').scrollIntoView({ behavior: 'smooth' })}
                >
                  Filter Activities
                </Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Sidebar with Filters */}
            <div className="lg:col-span-3" id="filters-section">
              <ActivityFilters 
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                activityTypes={activityTypesList}
              />
            </div>

            {/* Main Content Area */}
            <div className="lg:col-span-9">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                  <span className="ml-4 text-text-secondary">Loading activities...</span>
                </div>
              ) : error ? (
                <div className="text-center py-16">
                  <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-text-primary mb-2">Error Loading Activities</h3>
                  <p className="text-text-secondary mb-4">{error}</p>
                  <Button variant="primary" onClick={fetchAllActivities}>
                    Try Again
                  </Button>
                </div>
              ) : filteredActivities.length === 0 ? (
                <div className="text-center py-16 bg-surface rounded-lg border border-border">
                  <Icon name="Inbox" size={48} className="text-text-secondary mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-text-primary mb-2">No Activities Found</h3>
                  <p className="text-text-secondary mb-4">
                    {activities.length === 0 
                      ? "You don't have any activities yet. Start by applying to jobs or updating your profile."
                      : "No activities match your current filters. Try adjusting your filters or clearing them."}
                  </p>
                  {activities.length > 0 && (
                    <Button variant="outline" onClick={handleClearFilters}>
                      Clear Filters
                    </Button>
                  )}
                </div>
              ) : (
                <ActivityTimeline 
                  activities={filteredActivities}
                  onActivityClick={handleActivityClick}
                />
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VAActivitiesPage;
